<script setup lang="ts">
import type { IntegrationPhaseSteps } from "@/types/integration"
import DialogHeader from "@/components/Integration/DialogHeader.vue"
import Loading from "@/components/Loading.vue"
import { Integration, useIntegrationStore } from "@/stores"
import { DialogSize, KDialog } from "@kaleidoscope-solutions/ks-design-system"
import { storeToRefs } from "pinia"
import { computed, ref } from "vue"

const integrationStore = useIntegrationStore()
const { running, stepper } = storeToRefs(integrationStore)
const disableImport = ref(false)

function closeDialog() {
  integrationStore.dialog = false
  // Reset Integration when dialog closes.
  integrationStore.reset()
}

const stepComponent = computed(() => {
  return stepper.value.current.value.component ?? undefined
})

const stepBindings = computed(() => {
  return {
    currentIntegration: integrationStore.currentIntegrationConfig,
  }
})

function handleNext(step: IntegrationPhaseSteps) {
  integrationStore.goToStep(step)
}

function handleStep(step: IntegrationPhaseSteps) {
  integrationStore.goToStep(step)
}

function handleDisableImport(isThousandLimitErr: boolean) {
  disableImport.value = isThousandLimitErr
}

async function finishImport() {
  integrationStore.setCurrentIntegration(Integration.Shopify)
  await integrationStore.runCurrentIntegration()
  closeDialog()
}

async function handleSubmit(token: string, url: string) {
  await integrationStore.updateIntegration({
    integration: Integration.Shopify,
    store_name: url,
    access_token: token,
  })
}
</script>

<template>
  <KDialog
    :open="true"
    :size="DialogSize.fullsize"
    :esc="false"
    @close="closeDialog"
  >
    <template #default>
      <DialogHeader
        :stepper="stepper"
        :running="running"
        @next="handleNext"
        @step="handleStep"
        @finish="finishImport"
      />
      <div class="flex h-full w-full overflow-auto pt-6">
        <div
          v-if="running"
          class="fixed inset-20 z-20 grid place-content-center gap-4"
        >
          <Loading class="h-40 w-40" />
          <div class="animate-pulse">
            Running integration ...
          </div>
        </div>
        <component
          :is="stepComponent"
          v-else-if="stepComponent"
          v-bind="stepBindings"
          @disable-import="handleDisableImport"
          @submit="handleSubmit"
        />
      </div>
    </template>
  </KDialog>
</template>
